import { useCallback, useSyncExternalStore } from 'react';

/**
 * Guest's client-side cached address (entered without signing in). Stored in
 * ``sessionStorage`` so it is erased on tab/browser close — matching the "not
 * persisted between sessions" commitment — but survives an in-session refresh
 * (exempted from the beforeunload wipe in ``storageUtils``) and is cleared on
 * sign-out. ``useSyncExternalStore`` gives tearing-safe reads across consumers
 * within the tab. Fields (``formatted_address`` + ``address_components``)
 * mirror what the signed-in profile persists.
 */
const STORAGE_KEY = 'votemate_guest_address';

export interface GuestAddress {
  formatted_address: string;
  // Keep the Google Places shape loose; the same blob is forwarded to the
  // backend on post-sign-in sync where it's already understood.
  address_components?: unknown[];
}

const readFromStorage = (): GuestAddress | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      'formatted_address' in parsed &&
      typeof (parsed as Record<string, unknown>).formatted_address === 'string' &&
      ((parsed as Record<string, unknown>).formatted_address as string).length > 0
    ) {
      return parsed as GuestAddress;
    }
    return null;
  } catch {
    return null;
  }
};

const persist = (addr: GuestAddress | null): void => {
  try {
    if (addr === null) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(addr));
    }
  } catch {
    // sessionStorage may be full / disabled (private mode, quota) — fail silently.
  }
};

// Module-level state shared across consumers, matching the useLikedCandidates pattern.
let guestAddress: GuestAddress | null = readFromStorage();
const subscribers = new Set<() => void>();

const subscribe = (callback: () => void): (() => void) => {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
};

const getSnapshot = (): GuestAddress | null => guestAddress;

/** Non-hook read of the cached guest address, for module-level bootstraps. */
export const getGuestAddress = getSnapshot;

const notify = (): void => {
  subscribers.forEach((cb) => cb());
};

// No cross-tab sync: ``sessionStorage`` is per-tab and does not emit the
// ``storage`` event to other tabs, so each tab keeps its own guest address.
// That's the intended scope for a transient, session-only guest value.

/**
 * Returns the current guest-cached address plus setter and clear helpers.
 *
 * Use ``setAddress`` to write (call after a successful address-autocomplete
 * selection); use ``clearAddress`` to remove (call on sign-in sync
 * completion, or when a signed-in user clears their address).
 */
const useGuestAddress = () => {
  const address = useSyncExternalStore(subscribe, getSnapshot);

  const setAddress = useCallback((addr: GuestAddress): void => {
    guestAddress = addr;
    persist(guestAddress);
    notify();
  }, []);

  const clearAddress = useCallback((): void => {
    guestAddress = null;
    persist(null);
    notify();
  }, []);

  return { address, setAddress, clearAddress };
};

export default useGuestAddress;
